import { useNuxtApp, type UseFetchOptions } from 'nuxt/app';
import { ref } from 'vue';
import type { $Fetch, NitroFetchRequest } from 'nitropack';
import { useStore } from '@/stores/store';

export default function useApiService() {
  const { loadArray } = storeToRefs(useStore());
  const { $customFetch } = useNuxtApp();

  const runWithAction = async <T>(action: string | undefined, fn: () => Promise<T>) => {
    if (action) loadArray.value.push(action);
    try {
      return await fn();
    } finally {
      if (action) {
        const actionIndex = loadArray.value.indexOf(action);
        if (actionIndex >= 0) loadArray.value.splice(actionIndex, 1);
      }
    }
  };

  const createQueryString = (queryOption?: URLSearchParams): string => {
    if (!queryOption) return '';
    if (typeof queryOption === 'string') return queryOption;
    if (queryOption instanceof URLSearchParams) return queryOption.toString();

    if (typeof queryOption === 'object') {
      try {
        return new URLSearchParams(queryOption).toString();
      } catch {
        return '';
      }
    }

    return '';
  };

  const appendQueryStringToUrl = (url: string, queryString: string) => {
    if (!queryString) return url;
    return url.includes('?') ? `${url}&${queryString}` : `${url}?${queryString}`;
  };

  const apiRequest = async <T>(url: string, options: UseFetchOptions<T>, action?: string) => {
    return await runWithAction(action, async () => {
      const requestMethod = (options.method ?? 'GET').toString().toUpperCase();
      const uniqueKey = `${requestMethod}:${url}:${Date.now()}:${Math.random()}`;
      const watch = options.watch ?? (requestMethod === 'GET' ? undefined : false);

      return await useFetch(url, {
        ...options,
        key: uniqueKey,
        cache: 'no-store',
        dedupe: 'cancel',
        watch,
        $fetch: $customFetch as $Fetch<unknown, NitroFetchRequest>
      });
    });
  };

  const get = async <T>(url: string, action?: string, options?: UseFetchOptions<T>) => {
    return await runWithAction(action, async () => {
      const data = ref<T | null>(null);
      const error = ref<unknown | null>(null);
      const pending = ref(true);

      const headers = options?.headers as HeadersInit | undefined;
      const queryOption = options?.query;
      const queryString = createQueryString(queryOption as URLSearchParams);
      const finalUrl = appendQueryStringToUrl(url, queryString);

      try {
        const result = await $customFetch(finalUrl, {
          method: 'GET',
          headers
        });
        data.value = result as T;
      } catch (caughtError: unknown) {
        error.value = createError(caughtError as Error);
      } finally {
        pending.value = false;
      }

      return { data, error, pending } as ReturnType<typeof useFetch<T>>;
    });
  };

  const post = async <T>(
    url: string,
    body: UseFetchOptions<T>['body'],
    action?: string,
    options?: UseFetchOptions<T>
  ) => {
    return await apiRequest(url, { ...options, method: 'POST', body }, action ?? '');
  };

  const put = async <T>(
    url: string,
    body: UseFetchOptions<T>['body'],
    action?: string,
    options?: UseFetchOptions<T>
  ) => {
    return await apiRequest(url, { ...options, method: 'PUT', body }, action ?? '');
  };

  const patch = async <T>(
    url: string,
    body: UseFetchOptions<T>['body'],
    action?: string,
    options?: UseFetchOptions<T>
  ) => {
    return await apiRequest(url, { ...options, method: 'PATCH', body }, action ?? '');
  };

  const del = async <T>(url: string, action?: string, options?: UseFetchOptions<T>) => {
    return await apiRequest(url, { ...options, method: 'DELETE' }, action ?? '');
  };

  return {
    get,
    post,
    put,
    patch,
    del
  };
}
